import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateProviderBlobConnection,
  IProviderBlobConnectionResponse,
  IUpdateProviderBlobConnection,
} from './types';

export const useGetProviderBlobConnection = (
  providerId: string,
  salesChannelId: string
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IProviderBlobConnectionResponse, DefaultError>({
    queryKey: ['blob-conection', providerId, salesChannelId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Providers/Connection/Blob`,
        params: { providerId, salesChannelId },
      });

      return responseHandler(response, {
        groupName: 'useGetProviderBlobConnections',
        notFoundPath: `/settings/provider/${providerId}/blob-connection/create/${salesChannelId}/keys`,
      });
    },
  });
};

export const usePostProviderBlobConnection = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostProviderBlobConnections'
  );

  return useMutation<
    IProviderBlobConnectionResponse,
    DefaultError,
    ICreateProviderBlobConnection
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: i18next.t('provider.blob-what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Providers/Connection/Blob`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePostProviderBlobConnections`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('provider.blob-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutProviderBlobConnection = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutProviderBlobConnection'
  );

  return useMutation<
    IProviderBlobConnectionResponse,
    DefaultError,
    IUpdateProviderBlobConnection
  >({
    mutationFn: async body => {
      loading(
        i18next.t('create.update.loading', {
          what: i18next.t('provider.blob-what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `Providers/Connection/Blob`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePutProviderBlobConnection`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('provider.blob-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteProviderBlobConnection = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteProviderBlobConnection'
  );

  return useMutation<
    boolean,
    DefaultError,
    { providerId: string; salesChannelId: string }
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('provider.blob-what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Providers/Connection/Blob`,
        body,
      });

      return responseHandler(response, {
        groupName: `useDeleteProviderBlobConnection`,
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('provider.blob-what'),
        })
      );
    },
    onError: dismiss,
  });
};
